// ** React Imports
import { Fragment, useState, useCallback, useEffect } from "react"
// ** Third Party Components
import { useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, Save } from "react-feather"
import { useForm, Controller } from "react-hook-form"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
// ** Utils
import { provideOptionsToSelect, checkFieldValidity } from "@lib/util"
import { putVisitForm, postVisitForm } from "@lib/api"
// ** Reactstrap Imports
import { Label, Row, Col, Button, Form, Input, FormText } from "reactstrap"
// ** Store Vars
import { useDispatch, useSelector } from "react-redux"
import { setFormData, resetFormAndPersonData } from "@store/stores/person"
import SelectShowroomListbox from "../../components/SelectShowroomListbox"

const OtherInfo = ({ stepper }) => {
  const MySwal = withReactContent(Swal)
  // ** Store Vars
  const personStore = useSelector((state) => state.persons)
  const userStore = useSelector((state) => state.users)
  // ** Vars
  const [selectData, setSelectData] = useState()
  // ** Hooks
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()

  // ** Functions
  const submitDataAjax = (concatPersonalData, apiCallFunc) => {
    MySwal.fire({
      title: "Confirm",
      text: "Are you sure you want to submit this form?",
      customClass: {
        input: "mx-3",
        confirmButton: "btn btn-success mx-1",
        cancelButton: "btn btn-danger ms-1"
      },
      buttonsStyling: false,
      confirmButtonText: "Confirm",
      showLoaderOnConfirm: true,
      preConfirm() {
        return apiCallFunc(
          concatPersonalData,
          personStore.formData.contactInfos.emails,
          personStore.formData.contactInfos.phones,
          personStore.formData.addresses,
          // optional fields during edit mode (PUT)
          params.action || "Edit", // what's the action in the slug
          personStore.selectedPerson?.id // we need the id of the person to edit
        )
      }
    }).then(function (res) {
      if (!res.isConfirmed) return
      const { value } = res
      if (value.status >= 200) {
        // prettier-ignore
        const text = value.status === 201 ? `${value.data.name} ${value.data.firstName} created.` : "Person updated."
        let resetUrlRedirect = "/CRM"
        // if an id is returned, we go to this newly created person page
        if (value.data?.id) resetUrlRedirect = `/CRM/Visits/${value.data.id}`
        // if we are in validate mode, we go back to the list
        if (params.action === "Validate") resetUrlRedirect = "/CRM/PersonsToValidate"
        // confirmation modal
        MySwal.fire({
          title: "Operation successful",
          text,
          icon: "success",
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            confirmButton: "btn btn-primary"
          }
        }).then(function (result) {
          if (result.isConfirmed || result.isDismissed) {
            dispatch(resetFormAndPersonData())
            navigate(resetUrlRedirect)
          }
        })
      }
    })
  }

  // fetch specific data
  const fetchSelectData = useCallback(async () => {
    const categories = await provideOptionsToSelect({ name: "Categories" })
    const tags = await provideOptionsToSelect({ name: "Tags" })
    const interactionOrigins = await provideOptionsToSelect({
      name: "Interactions/Origins",
      apiEndpoint: "visits"
    })
    const interactionStates = await provideOptionsToSelect({
      name: "Interactions/States",
      apiEndpoint: "visits"
    })
    setSelectData({
      categories,
      tags,
      // filter out < 10 to leave a margin for later uses
      interactionOrigins: interactionOrigins.filter((origin) => origin.value >= 10),
      interactionStates
    })
  }, [])

  useEffect(() => {
    fetchSelectData()
  }, [fetchSelectData])

  // default form values
  let defaultValues = {}
  if (personStore.selectedPerson) {
    defaultValues = {
      categories: personStore.selectedPerson.categories.map((category) => ({
        label: category.name,
        value: category.id
      })),
      tags: personStore.selectedPerson.tags.map((tag, index) => ({
        label: tag,
        value: index
      })),
      state: {
        label: personStore.selectedPerson.interaction?.interactionState.name,
        value: personStore.selectedPerson.interaction?.interactionState.id
      },
      origin: {
        label: personStore.selectedPerson.interaction?.interactionOrigin.name,
        value: personStore.selectedPerson.interaction?.interactionOrigin.id
      },
      remarks: personStore.selectedPerson.remarks || ""
    }
  } else {
    defaultValues = {
      categories: [],
      tags: [],
      remarks: ""
    }
  }
  const {
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues })

  const onSubmit = async (data) => {
    // form validation
    let error = false
    const optionalFields = ["remarks", "tags", "origin", "showroomId"]
    for (const key in data) {
      if (optionalFields.includes(key)) continue
      if (!checkFieldValidity(data[key])) {
        setError(key, {
          type: "manual",
          message: `'${key}' is required`
        })
        error = true
      }
    }
    if (error) return

    // check if user account has all required fields
    if (
      Object.hasOwn(
        userStore.selectedUser,
        "collaboratorId" && "internalCompanyId" && "showroomId"
      ) &&
      (!userStore.selectedUser.collaboratorId ||
        !userStore.selectedUser.internalCompanyId ||
        !userStore.selectedUser.showroomId)
    ) {
      MySwal.fire({
        title: "Error",
        allowOutsideClick: false,
        html: `
        <div>
          <p>Your account is missing the following information :</p>
          ${!userStore.selectedUser.collaboratorId ? "<p>collaboratorId</p>" : ""}
          ${!userStore.selectedUser.internalCompanyId ? "<p>internalCompanyId</p>" : ""}
          ${!userStore.selectedUser.showroomId ? "<p>showroomId</p>" : ""}
        </div>`,
        icon: "error"
      })
      return
    }

    // clean and update store values
    const cleanData = {
      categories: data.categories.map((category) => {
        return { id: category.value }
      }),
      interactionStateId: data.state.value,
      // if empty, auto default to 1 (https://elitesa.atlassian.net/browse/OPUS-335)
      interactionOriginId: data.origin?.value || 1,
      tags: data.tags?.map((tag) => tag.label),
      remarks: data.remarks,
      internalCompanyId: userStore.selectedUser.internalCompanyId,
      collaboratorId: userStore.selectedUser.collaboratorId,
      showroomId: data.showroomId || userStore.selectedUser.showroomId
    }
    dispatch(
      setFormData({
        ...personStore.formData,
        otherInfos: cleanData
      })
    )
    // concat data
    const concatPersonalData = {
      ...personStore.formData.personalInfos,
      ...cleanData
    }
    // ! Final submit : PUT if id is present, POST otherwise
    submitDataAjax(concatPersonalData, personStore.selectedPerson ? putVisitForm : postVisitForm)
  }

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="content-header">
            <h5 className="mb-0">Others</h5>
            <small>Other information about the person</small>
          </div>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="primary"
              className="btn-prev"
              onClick={() => stepper.previous()}
            >
              <ArrowLeft size={14} className="align-middle me-sm-25 me-0"></ArrowLeft>
              <span className="align-middle d-sm-inline-block d-none">Previous</span>
            </Button>
            <Button type="submit" color="success" className="btn-submit">
              {params.action === "Validate" ? "Save and validate" : "Submit"}
              <Save size={16} className="align-middle ms-sm-25 ms-0"></Save>
            </Button>
          </div>
        </div>
        <hr />

        <Row>
          <Col className="mb-1" md="6" sm="6">
            <Label className="form-label" for="categories">
              Categories *
            </Label>
            <Controller
              id="categories"
              name="categories"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={selectData?.categories}
                  className="react-select"
                  classNamePrefix="select"
                  isSearchable={false}
                  isMulti
                />
              )}
            />
            {errors.categories && (
              <small style={{ color: "#ea5455" }}>{errors.categories.message}</small>
            )}
          </Col>
          <Col className="mb-1" md="6" sm="6">
            <Label className="form-label" for="state">
              Type *
            </Label>
            <Controller
              id="state"
              name="state"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={selectData?.interactionStates}
                  className="react-select"
                  classNamePrefix="select"
                  isSearchable={false}
                />
              )}
            />
            {errors.state && <small style={{ color: "#ea5455" }}>{errors.state.message}</small>}
          </Col>
        </Row>
        <Row>
          <Col className="mb-1" md="6" sm="6">
            <Label className="form-label" for="origin">
              How does the person know about us?
            </Label>
            <Controller
              id="origin"
              name="origin"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={selectData?.interactionOrigins}
                  className="react-select"
                  classNamePrefix="select"
                  isSearchable={false}
                />
              )}
            />
          </Col>
          <Col className="mb-1" md="6" sm="6">
            <Label className="form-label" for="tags">
              Tags
            </Label>
            <Controller
              id="tags"
              name="tags"
              control={control}
              render={({ field }) => (
                <CreatableSelect
                  {...field}
                  options={selectData?.tags}
                  className="react-select"
                  classNamePrefix="select"
                  isMulti
                />
              )}
            />
            <FormText className="text-muted">You can add new tags</FormText>
          </Col>
        </Row>
        <Row>
          <Col md="12" className="mb-1">
            <Label className="form-label" for="remarks">
              Remarks on the person
            </Label>
            <Controller
              id="remarks"
              name="remarks"
              control={control}
              render={({ field }) => (
                <Input type="textarea" placeholder="Eventual remarks on the person" {...field} />
              )}
            />
          </Col>
        </Row>
        <hr className="my-3" />

        {params.action !== "Edit" && userStore.selectedUser && (
          <Row>
            <Col md="6" className="mb-1">
              <SelectShowroomListbox user={userStore.selectedUser} control={control} />
            </Col>
          </Row>
        )}
      </Form>
    </Fragment>
  )
}

export default OtherInfo
